<template>
  <b-container fluid>
    <admin-preloader :show="loading"/>
    <admin-breadcrumb :items="[{ text: 'Pricing Plans', path: {name: 'admin-pricing-plans'} }]" :name="pricingPlan ? pricingPlan.name : 'Edit Pricing Plan'" no-title/>
    <b-row v-if="!loading">
      <b-col lg="4" md="5">
        <admin-panel sub-title="Companies">
          <admin-companies-table v-if="companies.length" :companies="companies" pricing-plan-view/>
          <h4 v-else class="mb-0">No companies connected to this plan</h4>
        </admin-panel>
        <admin-panel sub-title="Delete Pricing Plan">
          <h4 class="mb-2">Warning:<br>After confirmation this action cannot be reversed</h4>
          <no-permission-wrapper :show="!canDeletePricingPlans">
            <b-btn
                @click.prevent="confirmDeletePricingPlan"
                variant="danger"
                :disabled="!canDeletePricingPlans"
            >
              Delete Plan
            </b-btn>
          </no-permission-wrapper>
        </admin-panel>
      </b-col>
      <b-col lg="8" md="7">
        <admin-panel with-footer sub-title="Edit Pricing Plan Data">
          <admin-pricing-plan-form v-model="formData" :validation="formValidation" :disabled="!canUpdatePricingPlans"/>

          <template v-slot:footer>
            <no-permission-wrapper :show="!canUpdatePricingPlans">
              <b-btn
                  @click.prevent="submitPricingPlan"
                  variant="primary"
                  :disabled="formState.processing || !canUpdatePricingPlans"
              >
                Update Plan
                <b-spinner v-if="formState.processing" class="ml-1" small/>
              </b-btn>
            </no-permission-wrapper>
          </template>
        </admin-panel>
      </b-col>
    </b-row>
    <b-modal
        v-if="pricingPlan"
        ref="deleteModal"
        id="delete-plan-modal"
        title="Please confirm"
        centered
        ok-title="Confirm"
        ok-variant="success"
        @ok="deletePricingPlan"
    >
      <h4>You are going to delete this pricing plan</h4>
      <template v-if="pricingPlan.companies_count > 0">
        <hr>
        <b-form-group label="Switch Companies to Different Plan">
          <b-form-checkbox v-model="switchCompaniesPlan">Yes</b-form-checkbox>
        </b-form-group>
        <b-form-group v-if="switchCompaniesPlan" label="Select Plan to Switch">
          <b-form-select
              v-model="planToSwitch"
              :options="planToSwitchOptions"
              :state="planToSwitchError ? false : null"
          />
          <b-form-invalid-feedback :state="planToSwitchError ? false : null">
            {{ planToSwitchError }}
          </b-form-invalid-feedback>
        </b-form-group>
      </template>
    </b-modal>
  </b-container>
</template>

<script>
import AdminBreadcrumb from "../components/AdminBreadcrumb.vue";
import AdminPanel from "../components/AdminPanel.vue";
import {API_ROUTES} from "../api";
import AdminPreloader from "../components/AdminPreloader.vue";
import AdminPricingPlanForm from "../components/AdminPricingPlanForm.vue";
import AdminCompaniesTable from "../components/AdminCompaniesTable.vue";
import NoPermissionWrapper from "../components/NoPermissionWrapper.vue";

export default {
  name: 'AdminPricingPlanView',
  components: {
    NoPermissionWrapper,
    AdminCompaniesTable, AdminPricingPlanForm, AdminPreloader, AdminPanel, AdminBreadcrumb},
  props: {
    id: {
      required: true
    }
  },
  data() {
    return {
      loading: true,
      pricingPlan: null,
      companies: [],
      formData: {},
      formState: {
        processing: false
      },
      formValidation: {

      },
      switchCompaniesPlan: false,
      planToSwitch: null,
      planToSwitchError: null,
      planToSwitchOptions: []
    }
  },
  computed: {
    canUpdatePricingPlans() {
      return this.$store.getters['auth/hasAdminPermissions']('pricing_plan_update');
    },
    canDeletePricingPlans() {
      return this.$store.getters['auth/hasAdminPermissions']('pricing_plan_delete');
    }
  },
  methods: {
    checkPermission(permission) {
      if (!this[permission]) {
        this.$bvToast.toast('Sorry! You don\'t have permission to do this action', {
          title: 'Denied!',
          variant: 'danger'
        });
        return false;
      }
      return true;
    },
    submitPricingPlan() {
      if (!this.checkPermission('canUpdatePricingPlans')) {
        return;
      }
      this.formState.processing = true;
      this.formValidation = {};
      this.$api.post(`${API_ROUTES.ADMIN_PRICING_PLANS}/${this.id}`, this.formData).then(({data}) => {
        this.$bvToast.toast('Pricing plan has been successfully updated', {
          title: 'Success!',
          variant: 'success'
        });
        this.pricingPlan = data.pricing_plan;
      }).catch(({response: {data}}) => {
        this.$bvToast.toast(data.message ?? 'Failed to update a pricing plan', {
          title: 'Error!',
          variant: 'danger'
        });
        let validationErrors = {};
        if (typeof data.errors !== 'undefined') {
          for (let fieldName in data.errors) {
            validationErrors[fieldName] = data.errors[fieldName].join(', ');
          }
        }
        this.formValidation = validationErrors;
      }).finally(() => {
        this.formState.processing = false;
      });
    },
    confirmDeletePricingPlan() {
      if (!this.checkPermission('canDeletePricingPlans')) {
        return;
      }
      this.$root.$emit('bv::show::modal', 'delete-plan-modal');
    },
    deletePricingPlan() {
      if (!this.checkPermission('canDeletePricingPlans')) {
        return;
      }
      this.loading = true;
      this.planToSwitchError = null;
      this.$api.post(`${API_ROUTES.ADMIN_PRICING_PLANS}/${this.id}/delete`, {
        is_switch_plan: this.switchCompaniesPlan,
        plan_to_switch: this.planToSwitch
      }).then(() => {
        this.$bvToast.toast('Pricing plan has been successfully deleted', {
          title: 'Success!',
          variant: 'success'
        });
        this.$router.replace({name: 'admin-pricing-plans'});
      }).catch(({response: {data}}) => {
        if (typeof data.errors !== 'undefined') {
          if (typeof data.errors.plan_to_switch !== 'undefined') {
            this.planToSwitchError = data.errors.plan_to_switch.join(', ');
          }
        }
        this.$bvToast.toast(data.message ?? 'Failed to process operation', {
          title: 'Error!',
          variant: 'danger'
        });
        setTimeout(() => {
          this.confirmDeletePricingPlan();
        }, 200);
      }).finally(() => {
        this.loading = false;
      });
    }
  },
  created() {
    this.$api.get(`${API_ROUTES.ADMIN_PRICING_PLANS}/${this.id}`).then(({data}) => {
      this.pricingPlan = data.pricing_plan;
      this.companies = data.pricing_plan.companies;

      this.$api.get(API_ROUTES.ADMIN_PRICING_PLANS, {
        params: {
          type: this.pricingPlan.type.code
        }
      }).then(({data}) => {
        this.planToSwitchOptions = data.pricing_plans.map(plan => ({
          value: plan.id,
          text: plan.name
        })).filter(o => o.value !== this.pricingPlan.id);
      }).catch(({response: {data}}) => {

      }).finally(() => {

      })
    }).catch(({response: {data}}) => {
      this.$bvToast.toast(data.message ?? 'Failed to load a pricing plan', {
        title: 'Error!',
        variant: 'danger'
      });
    }).finally(() => {
      this.loading = false
    })
  },
  watch: {
    pricingPlan(pricingPlan) {
      if (pricingPlan) {
        this.formData = {
          type: pricingPlan.type.code,
          name: pricingPlan.name,
          company_type: pricingPlan.company_type.code,
          rate: pricingPlan.is_free ? 'free' : 'paid',
          amount: pricingPlan.amount,
          is_public: pricingPlan.is_public,
          is_hub_access: pricingPlan.is_hub_access,
          hub_addons: pricingPlan.hub_addons.map(a => a.code),
          is_rates_by_user: pricingPlan.is_rates_by_user,
          rates_by_user: pricingPlan.rates_by_user.map(u => ({
            type: u.type,
            amount: u.amount
          })),
          is_rates_by_limit: pricingPlan.is_rates_by_limit,
          rates_by_limit: pricingPlan.rates_by_limit.map(l => ({
            type: l.type,
            from: l.limit_from,
            to: l.limit_to,
            amount: l.amount
          })),
          is_additional_charges: pricingPlan.is_additional_charges,
          additional_charges: pricingPlan.additional_charges,
          billing_frequency: pricingPlan.billing_frequency || 'one-time',
          billing_recurrence: pricingPlan.billing_recurrence || 'week',
          is_free_trial: pricingPlan.is_free_trial,
          free_trial_length: pricingPlan.free_trial_length || 1,
          free_trial_period: pricingPlan.free_trial_period || 'day',
          is_limit_licenses: pricingPlan.is_limit_licenses,
          licenses_limit: pricingPlan.licenses_limit,
          allow_extra_licenses: pricingPlan.allow_extra_licenses,
          extra_license_price: pricingPlan.extra_license_price,
        };
      }
    }
  }
}
</script>
